import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { Alert, buttonVariants, Card, Input, Separator, TextField } from '@heroui/react';
import type { PublicConfigResponse } from '@vlastiq/contracts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { fetchPublicConfig, postDevLogin } from '../auth/api.ts';
import { TelegramLoginButton } from '../components/TelegramLoginButton.tsx';
import { ActionButton } from '../components/ActionButton.tsx';
import { Logo } from '../components/Logo.tsx';
import { useAction } from '../lib/useAction.ts';

/** Maps the ?error= value the callback route redirects with. */
const ERROR_MESSAGES: Record<string, string> = {
  invalid: 'Не удалось подтвердить попытку входа. Попробуйте ещё раз.',
  expired: 'Попытка входа заняла слишком много времени. Попробуйте ещё раз.',
  cancelled: 'Вход отменён.',
  telegram_unavailable:
    'Не удалось связаться с Telegram, чтобы подтвердить членство в группе. Попробуйте чуть позже.',
};

export function LoginPage() {
  const { status, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState<PublicConfigResponse | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    fetchPublicConfig()
      .then(setConfig)
      .catch(() => setConfigError('Не удалось загрузить настройки входа.'));
  }, []);

  if (status === 'authenticated') return <Navigate to="/" replace />;
  if (status === 'not_a_member') return <Navigate to="/not-a-member" replace />;

  const errorKey = searchParams.get('error');
  const errorMessage = errorKey ? (ERROR_MESSAGES[errorKey] ?? ERROR_MESSAGES.invalid) : null;

  return (
    <div className="grid min-h-dvh place-items-center bg-background px-4 py-6 text-foreground">
      <Card className="w-full max-w-md">
        <Card.Header className="flex flex-row items-center gap-3">
          {/* The mark, not the word. It is the first thing anyone sees and the
              only branding on this page. */}
          <Logo className="size-12 shrink-0 rounded-xs" alt="NN" />
          <div className="min-w-0">
            <Card.Title className="text-xl">NN</Card.Title>
            <Card.Description>Доступ только для участников группы.</Card.Description>
          </div>
        </Card.Header>

        <Card.Content className="flex flex-col gap-4">
          {errorMessage && <Alert status="danger">{errorMessage}</Alert>}
          {configError && <Alert status="danger">{configError}</Alert>}

          {/* OIDC is a plain link — the server builds the authorization URL,
              including PKCE and state, so nothing sensitive is constructed here.
              A real <a> rather than a Button with an onPress: this is a
              navigation, and HeroUI's Button is RAC's, which only ever renders
              a <button>. `buttonVariants` gives it the same look. */}
          {config?.loginMode === 'oidc' && (
            <a className={buttonVariants({ fullWidth: true })} href={config.oidcStartPath}>
              Войти через Telegram
            </a>
          )}

          {/* Legacy fallback, rendered only when no OIDC credentials are set. */}
          {config?.loginMode === 'widget' && (
            <TelegramLoginButton
              botUsername={config.botUsername}
              authUrl={config.loginCallbackUrl}
            />
          )}

          {config?.devLoginEnabled && <DevLogin onSuccess={() => void refresh()} />}
        </Card.Content>
      </Card>
    </div>
  );
}

/**
 * Local development shortcut.
 *
 * Only rendered when the server reports `devLoginEnabled`, which is false in
 * production. It skips the Login Widget (which cannot render on localhost)
 * but NOT the membership check — you can only sign in as a Telegram user who
 * is genuinely in the group.
 *
 * TODO(OPEN-ITEM-A): confirm this is the local-dev approach you want.
 */
function DevLogin({ onSuccess }: { onSuccess: () => void }) {
  const [value, setValue] = useState('');
  const action = useAction();

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    void action.run(
      'dev-login',
      async () => {
        await postDevLogin(Number(value));
        onSuccess();
      },
      // No success message: the whole page is replaced by the app on success,
      // so a toast confirming it would arrive over the destination.
      { error: 'Вход для разработки не удался.' },
    );
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <Separator />
      <p className="text-sm text-muted">
        <strong className="text-foreground">Только для разработки.</strong> Вход по Telegram-id.
        Проверка членства в группе всё равно выполняется.
      </p>
      <div className="flex gap-2">
        {/* TextField is the RAC field wrapper; the actual control is the
            nested Input, which is where native attributes belong. */}
        <TextField aria-label="Telegram-id" value={value} onChange={setValue} isRequired fullWidth>
          <Input inputMode="numeric" placeholder="Telegram-id" />
        </TextField>
        <ActionButton
          type="submit"
          isPending={action.isPending('dev-login')}
          isDisabled={action.isBusy || value.trim() === ''}
        >
          Войти
        </ActionButton>
      </div>
    </form>
  );
}
